/**
 * The reviewer console.
 *
 * Separate from the contributor app in every way that matters: its own
 * credentials (a reviewer's SpeedRadio token, or the shared admin key for
 * automation), kept in sessionStorage so a shared ops machine does not hold
 * an admin key after the tab closes.
 *
 * This exists so the approval loop can be exercised end to end without a
 * second tool. It is a working console, not a finished back-office product.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Layout } from "../components/Layout";
import { useToast } from "../hooks/useToast";
import { reviews as reviewsApi, type ReviewCreds } from "../api/endpoints";
import { clearReviewCredentials, readReviewCredentials } from "../lib/session";
import { isCredentialFailure } from "../lib/backOffice";
import { ReviewSignIn } from "../components/ReviewSignIn";
import { BackOfficeNav } from "../components/BackOfficeNav";
import { Meter, MeterRow } from "../components/Meter";
import { Pagination } from "../components/Pagination";
import { RefreshButton } from "../components/RefreshButton";
import { ReviewDecision } from "../components/ReviewDecision";
import { useRefreshOnFocus } from "../hooks/useRefreshOnFocus";
import { formatDate, formatDuration } from "../lib/validation";
// Aliased: `ReviewDecision` is also the component that renders the
// buttons, and the two meeting in one file reads as a mistake.
import type {
  ReviewCard,
  ReviewDecision as Decision,
  ReviewStats,
  Whoami,
} from "../types/api";

export function Review() {
  const toast = useToast();
  const [creds, setCreds] = useState<ReviewCreds | null>(() => readReviewCredentials());
  const [who, setWho] = useState<Whoami | null>(null);
  const [stats, setStats] = useState<ReviewStats | null>(null);
  const [queue, setQueue] = useState<ReviewCard[]>([]);
  // Starts true when credentials are already stored, so the first paint is
  // the spinner rather than a flash of the empty queue.
  const [loading, setLoading] = useState(() => readReviewCredentials() !== null);
  const [deciding, setDeciding] = useState<string | null>(null);
  const [playingAudioId, setPlayingAudioId] = useState<string | null>(null);
  const activeAudioRef = useRef<HTMLAudioElement | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // The queue serves 25 at a time and always had more behind it; this
  // screen only ever showed the first page, so anything past 25 was
  // invisible and the backlog looked smaller than it was.
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);

  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((value) => value + 1), []);
  const handleAudioPlay = useCallback((recordingId: string, audio: HTMLAudioElement) => {
    if (activeAudioRef.current && activeAudioRef.current !== audio) {
      activeAudioRef.current.pause();
    }
    activeAudioRef.current = audio;
    setPlayingAudioId(recordingId);
  }, []);
  const handleAudioPause = useCallback((audio: HTMLAudioElement) => {
    if (activeAudioRef.current !== audio) return;
    activeAudioRef.current = null;
    setPlayingAudioId(null);
  }, []);

  // The fetch lives in the effect so every state update follows an await and
  // is skipped once cancelled.
  useEffect(() => {
    if (!creds) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadError(null);
        const [identity, statsResult, queueResult] = await Promise.all([
          reviewsApi.whoami(creds),
          reviewsApi.stats(creds),
          reviewsApi.queue(creds, { page }),
        ]);
        if (cancelled) return;
        setWho(identity);
        setStats(statsResult);
        // A queue response can contain freshly signed playback URLs. Don't
        // replace them under an active player, which would restart the clip.
        if (!activeAudioRef.current || activeAudioRef.current.paused) {
          setQueue(queueResult.recordings);
        }
        setHasMore(queueResult.has_more);
        setTotal(queueResult.total);
      } catch (err) {
        if (cancelled) return;
        toast.show((err as Error).message, "error");
        // Only a rejected key sends you back to sign-in. Anything else — the
        // API restarting, a dropped connection — keeps the credentials, or a
        // blip would log the reviewer out mid-shift.
        if (isCredentialFailure(err)) {
          clearReviewCredentials();
          setCreds(null);
          setWho(null);
        } else {
          setLoadError((err as Error).message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [creds, nonce, page, toast]);

  // Reviewers sit on this page waiting for work, so it polls as well as
  // refreshing on return — but never while a decision is being written or a
  // clip is playing.
  useRefreshOnFocus(reload, {
    enabled: deciding === null && playingAudioId === null,
    intervalMs: 60_000,
  });

  async function handleDecision(
    recordingId: string,
    decision: Decision,
    reason?: string,
    note?: string,
  ) {
    if (!creds) return;
    setDeciding(recordingId);
    try {
      await reviewsApi.decide(creds, recordingId, { decision, reason, note });
      toast.show(
        decision === "approved"
          ? "Approved — the contributor has been paid."
          : decision === "rerecord"
            ? "Sent back for another take."
            : "Rejected.",
        "success",
      );
      // Deciding the last item on a page would otherwise leave the reader
      // looking at an empty page with work still behind them.
      if (queue.length === 1 && page > 1) setPage((value) => value - 1);
      else reload();
    } catch (err) {
      toast.show((err as Error).message, "error");
    } finally {
      setDeciding(null);
    }
  }

  if (!creds) return <ReviewSignIn onSubmit={setCreds} />;

  return (
    <Layout showNav={false} showAccount={false} title="Review">
      <section className="section">
        <div className="wrap">
          <BackOfficeNav />

          <div className="row-between" style={{ marginBottom: 18 }}>
            <div>
              <div className="eyebrow">Review queue</div>
              <h1 className="display" style={{ fontSize: "clamp(22px,4vw,32px)", margin: 0 }}>
                {stats ? `${stats.pending} waiting` : "Review queue"}
              </h1>
              {who && (
                <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                  {who.is_service ? "Signed in with the shared admin key" : `${who.name ?? who.id}`}
                  {who.roles.length > 0 && ` · ${who.roles.join(", ")}`}
                </div>
              )}
            </div>
            <RefreshButton onClick={reload} busy={loading || playingAudioId !== null} />
          </div>

          <MeterRow>
            <Meter label="Approved" value={stats?.approved} />
            <Meter label="On air" value={stats?.published} />
            <Meter
              label="Publish blocked"
              value={
                stats ? stats.publish_blocked + stats.publish_failed : undefined
              }
              tone={stats?.publish_blocked ? "warn" : undefined}
            />
          </MeterRow>

          {loading && !stats ? (
            <div className="panel empty mt-24">
              <div style={{ display: "grid", placeItems: "center", gap: 12 }}>
                <div className="spinner" />
                <div className="muted" style={{ fontSize: 13 }}>
                  Loading the queue…
                </div>
              </div>
            </div>
          ) : loadError ? (
            <div className="panel empty mt-24">
              <h3>Couldn't load the queue</h3>
              <p className="muted" style={{ margin: "6px 0 0" }}>{loadError}</p>
              <button type="button" className="btn btn-ghost mt-16" onClick={reload}>
                Try again
              </button>
            </div>
          ) : queue.length === 0 ? (
            <div className="panel empty mt-24">
              <h3>Queue is clear</h3>
              <p className="muted" style={{ margin: "6px 0 0" }}>
                Nothing is waiting for review.
              </p>
              <button
                type="button"
                className="btn btn-ghost mt-16"
                onClick={reload}
              >
                Refresh
              </button>
            </div>
          ) : (
            <div className="stack mt-24">
              {queue.map((item) => (
                <ReviewRow
                  key={item.recording_id}
                  card={item}
                  busy={deciding === item.recording_id}
                  onDecide={handleDecision}
                  playingAudioId={playingAudioId}
                  onAudioPlay={handleAudioPlay}
                  onAudioPause={handleAudioPause}
                />
              ))}
            </div>
          )}

          <Pagination
            page={page}
            hasMore={hasMore}
            total={total}
            noun="in the queue"
            busy={loading || playingAudioId !== null}
            onChange={setPage}
            // Oldest first, so page 1 is what to review next and "forward"
            // means further back in time.
            labels={{ back: "← Newer", forward: "Older →" }}
          />

        </div>
      </section>
    </Layout>
  );
}

function ReviewRow({
  card,
  busy,
  onDecide,
  playingAudioId,
  onAudioPlay,
  onAudioPause,
}: {
  card: ReviewCard;
  busy: boolean;
  onDecide: (
    id: string,
    decision: Decision,
    reason?: string,
    note?: string,
  ) => Promise<void>;
  playingAudioId: string | null;
  onAudioPlay: (recordingId: string, audio: HTMLAudioElement) => void;
  onAudioPause: (audio: HTMLAudioElement) => void;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio && playingAudioId !== null && playingAudioId !== card.recording_id) {
      audio.pause();
    }
  }, [card.recording_id, playingAudioId]);

  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      if (audio) {
        audio.pause();
        onAudioPause(audio);
      }
    };
  }, [onAudioPause]);

  return (
    <article className="panel review-row">
      <div className="row-between" style={{ alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <div className="log-title">{card.title}</div>
          <div className="log-meta muted">
            Response #{card.response_number} · {card.contributor_name ?? card.contributor_id} ·{" "}
            {card.contributor_gender} · {formatDuration(card.duration_seconds)} ·{" "}
            {formatDate(card.submitted_at)}
          </div>
        </div>
        <div className="log-pills">
          {card.attempt > 1 && <span className="pill pill-rerecord">Take {card.attempt}</span>}
          <span className="pill">${card.compensation.formatted}</span>
        </div>
      </div>

      {card.prompt_text && (
        <div className="review-prompt">
          <span className="muted" style={{ fontSize: 11 }}>They were asked to react to</span>
          <p style={{ margin: "4px 0 0" }}>{card.prompt_text}</p>
        </div>
      )}

      {card.playback_url && (
        <audio
          ref={audioRef}
          className="playback"
          controls
          src={card.playback_url}
          preload="none"
          onPlay={(event) => onAudioPlay(card.recording_id, event.currentTarget)}
          onPause={(event) => onAudioPause(event.currentTarget)}
          onEnded={(event) => onAudioPause(event.currentTarget)}
          onError={(event) => onAudioPause(event.currentTarget)}
        />
      )}

      {card.transcript && <p className="log-text">"{card.transcript}"</p>}

      <div className="muted" style={{ fontSize: 12 }}>
        This contributor: {card.contributor_approved_count} approved,{" "}
        {card.contributor_rejected_count} rejected
      </div>

      <ReviewDecision recordingId={card.recording_id} busy={busy} onDecide={onDecide} />
    </article>
  );
}
